import clsx from "clsx";
import React from "react";
import { useNavigate } from "react-router";
import { getEnv, getSettings } from "src/services/configService";
import UserSettingsService from "src/services/userSettingsService";
import { useRecommendedPathForUser } from "src/ui/navigation/dashboardRouting/useRecommendedPathForUser";

import { useCurrentSubscriptionExpired } from "@dashboard/workspaces/api/helpers";
import Divider from "@mui/material/Divider";
import MuiDrawer from "@mui/material/Drawer";
import { Logo } from "@shared/Logo/Logo";

import { useStyles } from "./Drawer.styles";
import { useWithVisibleNavigation } from "./Drawer.utils";
import { DrawerToogler } from "./DrawerToogler";

type DrawerContextType = { open: boolean; setOpen: React.Dispatch<React.SetStateAction<boolean>> };

const DrawerContext = React.createContext<DrawerContextType>({
    open: false,
    setOpen: () => null,
});

export function useDrawerContext() {
    const context = React.useContext(DrawerContext);
    if (!context) {
        throw new Error("useDrawerContext must be used within a DrawerContextProvider");
    }
    return context;
}

type Props = React.PropsWithChildren<{ navigationContent: React.ReactNode }>;

export const Drawer = (props: Props) => {
    const [open, setOpen] = React.useState(UserSettingsService.getDrawerOpen());

    const isSubscriptionExpired = useCurrentSubscriptionExpired();
    const { path: recommendedPath, isLoading: isRecommendedPathLoading } = useRecommendedPathForUser();
    const { classes } = useStyles();
    const navigate = useNavigate();
    const env = getEnv();
    const { features } = getSettings();
    const hasVisibleNavigation = useWithVisibleNavigation();

    if (!hasVisibleNavigation) {
        return <div className={classes.content}>{props.children}</div>;
    }

    return (
        <DrawerContext.Provider value={{ open, setOpen }}>
            <div className={classes.root}>
                <MuiDrawer
                    variant="permanent"
                    className={clsx(classes.drawer, {
                        [classes.drawerOpen]: open,
                        [classes.drawerClose]: !open,
                    })}
                    classes={{
                        paper: clsx({
                            [classes.drawerOpen]: open,
                            [classes.drawerClose]: !open,
                        }),
                    }}
                >
                    <div className={classes.logoBox}>
                        <a
                            href={isRecommendedPathLoading ? "#" : recommendedPath}
                            onClick={(e) => {
                                e.preventDefault();
                                if (isRecommendedPathLoading) {
                                    return;
                                }
                                void navigate(recommendedPath);
                            }}
                        >
                            <Logo drawerOpen={open} />
                        </a>
                        {features.enviromentLabelEnabled && open ? <div className={classes.envLabel}>{env}</div> : null}
                        <DrawerToogler />
                    </div>
                    <Divider />
                    {props.navigationContent}
                </MuiDrawer>
                <div className={classes.content}>
                    <div className={isSubscriptionExpired ? classes.mainSubscriptionExpired : classes.main}>{props.children}</div>
                </div>
            </div>
        </DrawerContext.Provider>
    );
};
